import { useCallback, useEffect, useMemo, useRef, type ReactNode } from "react";
import { api, type Activity } from "../net/api.js";
import { useSession } from "../net/session.js";
import { useStore } from "../net/store.js";
import { PlayerChannel, type ConnectionState } from "../net/PlayerChannel.js";
import {
  applyEnvelopes, initialState, playerStore, type PlayerState,
} from "../net/playerStore.js";

/**
 * Owns the player's channel for as long as they are signed in.
 *
 * The shape is: bootstrap once, then listen. What used to be five independent
 * polling loops — activity every 15 seconds (every 750ms while queueing), the
 * ready room twice a second, the party every three, invitations in three
 * separate components — is one socket and one store.
 *
 * The socket is never trusted as the source of truth. It carries copies of rows
 * already committed in D1, so anything it misses is recovered from
 * `/channel/sync`, and a client that cannot hold a socket at all still works
 * on the slow path below.
 */

/**
 * How often to reconcile while disconnected.
 *
 * This is the deliberate fallback. If the channel is broken in production —
 * bad deploy, a network that eats WebSockets — the app degrades to a poll far
 * slower than the one it replaced rather than going deaf. It runs only while
 * the connection is not `live`.
 */
const RECOVERY_POLL_MS = 30_000;

/** After this long hidden, assume we missed something and reconcile on return. */
const STALE_AFTER_MS = 60_000;

export function ChannelProvider({ children }: { children: ReactNode }) {
  const session = useSession();
  const signedIn = session.status === "signedIn";
  const userId = signedIn ? session.me.user.id : null;
  const channelRef = useRef<PlayerChannel | null>(null);
  const hiddenSince = useRef<number | null>(null);
  const syncInFlight = useRef<Promise<void> | null>(null);
  const syncAgain = useRef(false);
  const liveSyncTimer = useRef<number | null>(null);

  /** Pull everything committed since our cursor. Safe to call at any time. */
  const sync = useCallback((): Promise<void> => {
    if (syncInFlight.current !== null) {
      syncAgain.current = true;
      return syncInFlight.current;
    }
    const running = (async () => {
      const channel = channelRef.current;
      if (channel === null) return;
      try {
        let cursor = playerStore.get().cursor;
        while (true) {
          const previous = cursor;
          const result = await api.sync(cursor);
          if (result.events.length > 0) {
            playerStore.set((state) => applyEnvelopes(state, result.events));
          }
          cursor = Math.max(cursor, result.cursor);
          // Advance only after every event in this scanned page was applied.
          playerStore.set((state) => ({ ...state, cursor: Math.max(state.cursor, cursor) }));
          channel.setCursor(cursor);
          if (!result.more || cursor <= previous) break;
        }
        const boot = await api.bootstrap();
        playerStore.set((state) => ({ ...state, unread: boot.unread }));
      } catch {
        // Offline, or the session expired. The next tick tries again.
      }
    })();
    const tracked = running.finally(() => {
      if (syncInFlight.current === tracked) syncInFlight.current = null;
      if (syncAgain.current) {
        syncAgain.current = false;
        void sync();
      }
    });
    syncInFlight.current = tracked;
    return tracked;
  }, []);

  useEffect(() => {
    if (!signedIn) {
      channelRef.current?.close();
      channelRef.current = null;
      playerStore.set(initialState);
      return;
    }

    let cancelled = false;
    const channel = new PlayerChannel({
      onEvents: (events) => {
        playerStore.set((state) => applyEnvelopes(state, events));
        // Batch bursts, then advance the durable cursor through `/sync`. This
        // keeps reconnect replay bounded without trusting publish RPC order.
        if (liveSyncTimer.current !== null) window.clearTimeout(liveSyncTimer.current);
        liveSyncTimer.current = window.setTimeout(() => {
          liveSyncTimer.current = null;
          void sync();
        }, 250);
      },
      onReady: (seq) => {
        // The server's log ends further along than we do: something landed
        // while we were away, so read it rather than wait for the next event.
        if (seq > playerStore.get().cursor) void sync();
      },
      onState: (connection: ConnectionState) => {
        playerStore.set((state) => (state.connection === connection ? state : { ...state, connection }));
      },
    });
    channelRef.current = channel;

    void (async () => {
      try {
        const boot = await api.bootstrap();
        if (cancelled) return;
        playerStore.set((state) => ({
          ...state,
          cursor: boot.cursor,
          unread: boot.unread,
          counts: boot.social,
          invitesById: Object.fromEntries(boot.invites.map((invite) => [invite.id, invite])),
        }));
        channel.start(boot.cursor);
      } catch {
        // Even a failed bootstrap should connect: the socket's `ready` frame
        // reports the server's cursor, which triggers a sync on its own.
        if (!cancelled) channel.start(0);
      }
      // The activity a player is recoverably in — a match they walked away
      // from, a lobby still open — is not an event, it is a starting position.
      try {
        const activity = await api.activity();
        if (!cancelled) playerStore.set((state) => ({ ...state, activity }));
      } catch { /* the recovery tick will pick it up */ }
    })();

    return () => {
      cancelled = true;
      if (liveSyncTimer.current !== null) window.clearTimeout(liveSyncTimer.current);
      liveSyncTimer.current = null;
      channel.close();
      channelRef.current = null;
    };
  }, [signedIn, userId, sync]);

  // The degraded path, and the one that makes a broken channel survivable.
  const connection = useStore(playerStore, (state) => state.connection);
  useEffect(() => {
    if (!signedIn || connection === "live") return;
    const id = window.setInterval(() => { void sync(); }, RECOVERY_POLL_MS);
    return () => window.clearInterval(id);
  }, [signedIn, connection, sync]);

  // Returning to a backgrounded tab. A socket the browser froze may still look
  // open, so reconcile rather than trust it.
  useEffect(() => {
    if (!signedIn) return;
    const onVisibility = () => {
      if (document.visibilityState === "hidden") { hiddenSince.current = Date.now(); return; }
      const away = Date.now() - (hiddenSince.current ?? Date.now());
      hiddenSince.current = null;
      if (away > STALE_AFTER_MS || playerStore.get().connection !== "live") void sync();
    };
    const onOnline = () => void sync();
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("online", onOnline);
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("online", onOnline);
    };
  }, [signedIn, sync]);

  return <>{children}</>;
}

/**
 * The player's current recoverable activity.
 *
 * Keeps the shape `useActivity()` always had, so no caller had to change when
 * the 15-second poll behind it became a pushed event.
 */
export function usePlayerActivity(): { activity: Activity; refresh: () => Promise<Activity | null> } {
  const activity = useStore(playerStore, (state) => state.activity);
  const refresh = useCallback(async () => {
    try {
      const next = await api.activity();
      playerStore.set((state) => ({ ...state, activity: next }));
      return next;
    } catch {
      return null;
    }
  }, []);
  return useMemo(() => ({ activity, refresh }), [activity, refresh]);
}

export function usePlayerState<T>(select: (state: PlayerState) => T): T {
  return useStore(playerStore, select);
}
